import { NumberXYValues } from 'Shared/molecules/Form/types'

import { AppDateFormats } from 'Core/constants'
import { ConfigItem } from 'Core/interfaces'
import { ConfigFilterItem } from 'Core/interfaces/filters'

import { ServiceLocation } from './ServiceLocation'

export enum OrderTrackingSearchFields {
	searchByOrderId = 'searchByOrderId',
	searchByItemId = 'searchByItemId',
	searchByOrderingContact = 'searchByOrderingContact',
	searchByServiceOccurrenceId = 'searchByServiceOccurrenceId',
	searchByServiceSiteId = 'searchByServiceSiteId',
	searchByServiceSiteName = 'searchByServiceSiteName'
}

export enum OrderTrackingSearchSuggestionsFields {
	orderId = 'orderId',
	itemId = 'itemId',
	serviceSiteId = 'serviceSiteId',
	serviceOccurrenceId = 'serviceOccurrenceId',
	contactName = 'contactName',
	serviceSiteName = 'serviceSiteName'
}

export enum OrderTrackingFilterFields {
	date = 'date',
	status = 'statusId',
	searchPeriod = 'searchPeriod',
	isToOpenPreview = 'isToOpenPreview'
}

export enum OrderTrackingFields {
	serviceSiteId = 'serviceSiteId',
	supplierLocationId = 'supplierLocationId',
	catalogItems = 'catalogItems',
	items = 'items'
}

export enum CatalogFormItemsFields {
	id = 'id',
	description = 'description',
	customer = 'customer',
	supplierLocation = 'supplierLocation',
	isPrebill = 'isPrebill',
	isDependent = 'isDependent',
	isScale = 'isScale',
	quantityUnits = 'quantityUnits',
	serviceDate = 'serviceDate',
	service = 'service',
	catalogId = 'catalogId',
	supplierCost = 'supplierCost',
	itemPrice = 'itemPrice',
	itemRefCode = 'itemRefCode',
	parentItemId = 'parentItemId',
	supplierItemCostId = 'supplierItemCostId',
	supplierItemId = 'supplierItemId',
	currentItemPriceId = 'currentItemPriceId',
	formQuantityUnits = 'formQuantityUnits',
	itemId = 'itemId',
	quantity = 'quantity',
	numberOfServices = 'numberOfServices',
	supplierAccountNumber = 'supplierAccountNumber',
	quantityCodeId = 'quantityCodeId',
	isSchedulingUsed = 'isSchedulingUsed',
	isAutoCharge = 'isAutoCharge'
}

export enum CompleteFormItemsFields {
	id = 'id',
	serviceOccurrenceId = 'serviceOccurrenceId',
	item = 'item',
	cost = 'cost',
	quantity = 'quantity',
	serviceDate = 'serviceDate',
	kilograms = 'kilograms',
	pounds = 'pounds',
	disposalFacilityId = 'disposalFacilityId',
	bol = 'bol',
	supplierTransactionCode = 'supplierTransactionCode',
	availableDisposalFacilities = 'availableDisposalFacilities',
	completeId = 'completeId',
	description = 'description',
	orderId = 'orderId',
	itemId = 'itemId',
	isPoundsMandatory = 'isPoundsMandatory',
	supplierAccountNumber = 'supplierAccountNumber',
	expectsSupplierInvoice = 'expectsSupplierInvoice'
}

export declare namespace OrderTracking {
	interface Basic {
		id: number
		serviceOccurrenceId: number
		serviceSite: ServiceLocation.AddressEntity | null
		status: string | null
		serviceDate: string
		orderingContactName: string | null
		items: Entity[]
		uom: string | null
		quantity: number
		effectiveDate: string
		endDate: string
		poAmount: number
		poDocument: string | null
		poNumber: string | null
	}

	namespace Search {
		type SuggestionResponse = Promise<{ items: Key[] | Entity[] }>

		interface Result {
			[OrderTrackingSearchFields.searchByOrderId]: number[]
			[OrderTrackingSearchFields.searchByItemId]: Entity[]
			[OrderTrackingSearchFields.searchByOrderingContact]: string[]
			[OrderTrackingSearchFields.searchByServiceOccurrenceId]: number[]
			[OrderTrackingSearchFields.searchByServiceSiteId]: Entity[]
			[OrderTrackingSearchFields.searchByServiceSiteName]: Entity[]
		}

		interface Config {
			[OrderTrackingSearchFields.searchByOrderId]: ConfigItem<OrderTrackingSearchFields>
			[OrderTrackingSearchFields.searchByItemId]: ConfigItem<OrderTrackingSearchFields>
			[OrderTrackingSearchFields.searchByOrderingContact]: ConfigItem<OrderTrackingSearchFields>
			[OrderTrackingSearchFields.searchByServiceOccurrenceId]: ConfigItem<OrderTrackingSearchFields>
			[OrderTrackingSearchFields.searchByServiceSiteId]: ConfigItem<
				OrderTrackingSearchFields,
				ServiceLocation.AddressEntity
			>
			[OrderTrackingSearchFields.searchByServiceSiteName]: ConfigItem<
				OrderTrackingSearchFields,
				ServiceLocation.AddressEntity
			>
		}
	}

	namespace Filters {
		interface Config {
			[OrderTrackingFilterFields.status]: ConfigFilterItem<string, null>
			[OrderTrackingFilterFields.date]: ConfigFilterItem<string, null>
			[OrderTrackingFilterFields.searchPeriod]: ConfigFilterItem<string, null>
			[OrderTrackingFilterFields.isToOpenPreview]: ConfigFilterItem<string, null>
		}
	}

	namespace CreateOrder {
		namespace CatalogItems {
			interface TableConfig<InputFieldNames extends string, DatepickerFields extends string> {
				inputs: Record<InputFieldNames, NumberXYValues>
				datepicker: Record<DatepickerFields, { getCurrentDateFormat: AppDateFormats }>
			}

			type FieldType = Record<CatalogFormItemsFields, Key | moment.Moment | null | boolean>

			interface Detailed {
				[CatalogFormItemsFields.id]: number
				[CatalogFormItemsFields.description]: string
				[CatalogFormItemsFields.customer]: Entity
				[CatalogFormItemsFields.supplierLocation]: Entity
				[CatalogFormItemsFields.isPrebill]: boolean
				[CatalogFormItemsFields.isDependent]: boolean
				[CatalogFormItemsFields.isScale]: boolean
				[CatalogFormItemsFields.quantityUnits]: BasicString
				[CatalogFormItemsFields.supplierCost]: string
				[CatalogFormItemsFields.serviceDate]: BasicString
				[CatalogFormItemsFields.service]: BasicString
				[CatalogFormItemsFields.itemPrice]: string
				[CatalogFormItemsFields.itemRefCode]: BasicString
				[CatalogFormItemsFields.parentItemId]: number
				[CatalogFormItemsFields.supplierItemCostId]: number
				[CatalogFormItemsFields.supplierItemId]: number
				[CatalogFormItemsFields.currentItemPriceId]: number
				[CatalogFormItemsFields.formQuantityUnits]: BasicString
				[CatalogFormItemsFields.supplierAccountNumber]: BasicString
				[CatalogFormItemsFields.quantityCodeId]: BasicNumber
				[CatalogFormItemsFields.isSchedulingUsed]: boolean
				[CatalogFormItemsFields.isAutoCharge]: boolean
			}
			namespace Form {
				interface Add extends Omit<Detailed, CatalogFormItemsFields.serviceDate> {
					[CatalogFormItemsFields.serviceDate]: moment.Moment | null
					[CatalogFormItemsFields.formQuantityUnits]: string | null
				}
			}
			namespace Request {
				interface Add {
					[CatalogFormItemsFields.serviceDate]: string
					[CatalogFormItemsFields.quantity]: string
					[CatalogFormItemsFields.itemId]: number
					[CatalogFormItemsFields.numberOfServices]: string
				}
			}
		}
		namespace Form {
			interface Add {
				[OrderTrackingFields.serviceSiteId]: Key | null
				[OrderTrackingFields.supplierLocationId]: Key | null
				[OrderTrackingFields.catalogItems]: CatalogItems.Form.Add[]
			}
		}

		namespace Request {
			interface Add {
				[OrderTrackingFields.serviceSiteId]: Key | null
				[OrderTrackingFields.items]: CatalogItems.Request.Add[]
			}
		}

		namespace Search {
			namespace ServiceSite {
				interface Result {
					searchById: ServiceLocation.AddressEntity[]
					searchByName: ServiceLocation.AddressEntity[]
				}
			}
			namespace SupplierLocation {
				interface Result {
					searchById: Entity[]
					searchByName: Entity[]
				}
			}
		}
		interface CreatedOrder {
			createdOrderIds: number[]
			failedItems: string[]
			suspiciousQuantities: number[]
		}
	}
	namespace CompleteOrder {
		interface TableConfig<
			SelectFieldNames extends string,
			InputFieldNames extends string,
			DatepickerFields extends string,
			SwitchFieldNames extends string
		> {
			selects: Record<
				SelectFieldNames,
				{
					options: LabelValue<number>[]
				}
			>
			inputs: Record<InputFieldNames, {}>
			datepicker: Record<DatepickerFields, { getCurrentDateFormat: AppDateFormats }>
			switches: Record<SwitchFieldNames, {}>
		}
		type FieldType = Record<CompleteFormItemsFields, Key | moment.Moment | null>

		interface Detailed {
			[CompleteFormItemsFields.id]: number
			[CompleteFormItemsFields.serviceOccurrenceId]: number
			[CompleteFormItemsFields.item]: Entity
			[CompleteFormItemsFields.cost]: string
			[CompleteFormItemsFields.quantity]: string
			[CompleteFormItemsFields.serviceDate]: BasicString
			[CompleteFormItemsFields.kilograms]: BasicString
			[CompleteFormItemsFields.pounds]: BasicString
			[CompleteFormItemsFields.disposalFacilityId]: number | null
			[CompleteFormItemsFields.availableDisposalFacilities]: Entity[]
			[CompleteFormItemsFields.bol]: BasicString
			[CompleteFormItemsFields.supplierTransactionCode]: BasicString
			[CompleteFormItemsFields.orderId]: number
			[CompleteFormItemsFields.isPoundsMandatory]: boolean
			[CompleteFormItemsFields.supplierAccountNumber]: BasicString
			[CompleteFormItemsFields.expectsSupplierInvoice]: boolean
		}
		interface CatalogItems extends Omit<Detailed, CompleteFormItemsFields.serviceDate> {
			[CompleteFormItemsFields.serviceDate]: moment.Moment | null
			[CompleteFormItemsFields.expectsSupplierInvoice]: boolean
		}
		namespace Form {
			interface Add {
				[OrderTrackingFields.catalogItems]: CatalogItems[]
			}
		}
		namespace Request {
			interface Add {
				[CompleteFormItemsFields.id]: number
				[CompleteFormItemsFields.serviceOccurrenceId]: number
				[CompleteFormItemsFields.itemId]: number
				[CompleteFormItemsFields.quantity]: string
				[CompleteFormItemsFields.serviceDate]: string
				[CompleteFormItemsFields.bol]: string
				[CompleteFormItemsFields.supplierTransactionCode]: string
				[CompleteFormItemsFields.kilograms]?: string
				[CompleteFormItemsFields.pounds]?: string
				[CompleteFormItemsFields.disposalFacilityId]: number | null
				[CompleteFormItemsFields.supplierAccountNumber]?: string
				[CompleteFormItemsFields.expectsSupplierInvoice]: boolean
			}
		}
	}
	namespace Detailed {
		interface OrderItem {
			item: Entity
			quantity: string
			estimatedTotal: string
			supplierAccountNumber: BasicString
		}

		interface SupplierOrderItem {
			catalogItemId: number
			supplierItemCostId: number
			supplierItemCostDescription: string
			quantity: number
			unitPrice: string
			unit: string
			estimatedTotal: string
		}
		interface SupplierOrder {
			id: number
			supplierLocation: Entity
			status: string
			finalDate: BasicString
			initialContactDate: BasicString
			confirmationDate: BasicString
			confirmationContact: string
			availableDate1: BasicString
			availableDate2: BasicString
			availableDate3: BasicString
			referenceNumber: string
			comments: string
			supplierOrderItems: SupplierOrderItem[]
			supplierOrderItemsEstimatedTotal: string
			defaultDisposalFacility: string
		}
		interface Basic {
			orderId: number
			serviceSite: Entity
			orderDate: BasicString
			hasSupplierInvoiceMatch: boolean
			status: string
			orderedBy: string
			placedBy: string
			beginServiceDate: BasicString
			endServiceDate: BasicString
			orderInstructions: string
			orderItems: OrderItem[]
			orderItemsEstimatedTotal: string
			isTurkish: boolean
			supplierOrders: {
				supplierType: string
				order: SupplierOrder
			}[]
		}
	}

	interface SearchPeriod {
		name: string
		to: string
		from: string
	}
}
